import {
  Box3,
  DoubleSide,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Texture,
  Vector3
} from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VoxelGridCalculator } from '../../utils/voxel-grid-calculator';
import { calculateBoundingBox } from '../helpers';
import { EngineContext } from './engine-context';

/**
 * Manages drill bit visualization and symmetry planes.
 * Extracted from Engine to reduce class size.
 */
export class DrillBitsManager {
  constructor(private ctx: EngineContext) {}

  generateSymmetryPlanes(maxSize: number, center: Vector3, size: Vector3): void {
    const createPlane = (
      planeSize: [number, number],
      rotateX?: number,
      rotateZ?: number,
      linePositions: number[][] = []
    ): Group => {
      const lineMaterial = new LineMaterial({ color: 0x000000, linewidth: 3 });
      lineMaterial.resolution.set(
        this.ctx.canvas.clientWidth * window.devicePixelRatio,
        this.ctx.canvas.clientHeight * window.devicePixelRatio
      );
      const planeMaterial = new MeshStandardMaterial({
        color: 0x000000,
        side: DoubleSide,
        transparent: true,
        opacity: 0.5
      });

      const group = new Group();
      const linesGroup = new Group();

      const geometry = new PlaneGeometry(planeSize[0], planeSize[1], 1, 1);
      if (rotateX !== undefined) geometry.rotateX(rotateX);
      if (rotateZ !== undefined) geometry.rotateZ(rotateZ);
      const mesh = new Mesh(geometry, planeMaterial);

      for (const positions of linePositions) {
        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(positions);
        const line = new Line2(lineGeometry, lineMaterial);
        line.computeLineDistances();
        line.scale.set(1, 1, 1);
        linesGroup.add(line);
      }

      group.add(mesh, linesGroup);
      group.visible = false;
      group.position.add(center);
      this.ctx.scene.add(group);
      return group;
    };

    // XY plane
    this.ctx.symmetryPlanes.xy = createPlane([size.x, size.y], undefined, undefined, [
      [size.x / 2, -size.y / 2, 0, size.x / 2, size.y / 2, 0],
      [-size.x / 2, -size.y / 2, 0, -size.x / 2, size.y / 2, 0],
      [-size.x / 2, -size.y / 2, 0, size.x / 2, -size.y / 2, 0],
      [-size.x / 2, size.y / 2, 0, size.x / 2, size.y / 2, 0]
    ]);

    // XZ plane
    this.ctx.symmetryPlanes.xz = createPlane([size.x, size.z], Math.PI * 0.5, undefined, [
      [size.x / 2, 0, -size.z / 2, size.x / 2, 0, size.z / 2],
      [-size.x / 2, 0, -size.z / 2, -size.x / 2, 0, size.z / 2],
      [-size.x / 2, 0, -size.z / 2, size.x / 2, 0, -size.z / 2],
      [-size.x / 2, 0, size.z / 2, size.x / 2, 0, size.z / 2]
    ]);

    // YZ plane
    this.ctx.symmetryPlanes.yz = createPlane([size.y, size.z], Math.PI * 0.5, Math.PI * 0.5, [
      [0, size.y / 2, -size.z / 2, 0, size.y / 2, size.z / 2],
      [0, -size.y / 2, -size.z / 2, 0, -size.y / 2, size.z / 2],
      [0, -size.y / 2, -size.z / 2, 0, size.y / 2, -size.z / 2],
      [0, -size.y / 2, size.z / 2, 0, size.y / 2, size.z / 2]
    ]);
  }

  disposeDrillBits(): void {
    const bits = this.ctx.drillBits;
    if (bits) {
      bits.x1 && (bits.x1.visible = false);
      bits.x2 && (bits.x2.visible = false);
      bits.y1 && (bits.y1.visible = false);
      bits.y2 && (bits.y2.visible = false);
      bits.z1 && (bits.z1.visible = false);
      bits.z2 && (bits.z2.visible = false);
    }
  }

  disposeSymmetryPlanes(): void {
    const sp = this.ctx.symmetryPlanes;
    if (sp.xy) sp.xy.visible = false;
    if (sp.xz) sp.xz.visible = false;
    if (sp.yz) sp.yz.visible = false;
  }

  async initDrillBits(): Promise<void> {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync('assets/webgl/drillbit.glb');
    const drillbitMesh: Mesh = gltf.scene.getObjectByProperty('type', 'Mesh') as Mesh;

    if (!drillbitMesh) return;

    drillbitMesh.geometry.center();
    drillbitMesh.geometry.computeBoundingBox();
    drillbitMesh.updateMatrixWorld(true);
    const material = new MeshPhysicalMaterial({
      color: 0xffffff,
      side: DoubleSide,
      vertexColors: false,
      flatShading: false,
      opacity: 1,
      transparent: true,
      metalness: 1,
      roughness: 0.2,
      envMap: (this.ctx as unknown as Record<string, unknown>)['envMap'] as Texture,
      envMapIntensity: 0.7
    });
    drillbitMesh.material = material;

    let ratio = 1;
    const objects = this.ctx.objects as { group: Group }[];
    if (objects.length > 0 && parseFloat(this.ctx.session.project.minimumToolSize as unknown as string) === 0) {
      try {
        const bboxDesign = calculateBoundingBox(objects.map((o) => o.group));
        const voxNum = 600000;
        const feSize = Math.round(
          Number.parseFloat((VoxelGridCalculator.getFeatureSize(bboxDesign, 3, voxNum) * 0.8).toFixed(2))
        );
        ratio = feSize;
        this.ctx.session.project.minimumToolSize = ratio;
      } catch (_) {
        // Use default ratio
      }
    }

    drillbitMesh.geometry.scale(ratio, ratio, ratio);
    this.ctx.drillbitMesh = drillbitMesh;

    const bits = this.ctx.drillBits;
    for (const key of Object.keys(bits)) {
      bits[key as keyof typeof bits] = drillbitMesh.clone(true);
      bits[key as keyof typeof bits]!.visible = false;
    }

    this.ctx.session.project.minimumToolSize ??= bits.x1!.scale.x;

    if (this.ctx.session.project.minimumToolSize) {
      this.drillBitScaleChange(this.ctx.session.project.minimumToolSize);
    }

    bits.x1!.rotateZ(-Math.PI / 2);
    bits.x2!.rotateZ(Math.PI / 2);
    bits.z1!.rotateX(Math.PI / 2);
    bits.z2!.rotateX(-Math.PI / 2);
    bits.y1!.rotateZ(0);
    bits.y2!.rotateZ(Math.PI);

    this.drillBitUpdate();
  }

  addDrillBitsToOutline(): void {
    if (!this.ctx.helpersVisible) return;
    for (const bit of Object.values(this.ctx.drillBits)) {
      if (!bit) continue;
      this.ctx.scene.add(bit);
      this.ctx.outlineEffect.selection.add(bit);
    }
  }

  removeDrillBitsFromOutline(): void {
    this.ctx.outlineEffect.selection.clear();
  }

  drillBitUpdate(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tweens = (this.ctx as unknown as Record<string, unknown>)['tweens'] as TWEEN.Tween<any>[];
    for (const tween of tweens) {
      tween.stop();
    }
    tweens.length = 0;

    const bits = this.ctx.drillBits;
    const { center, size } = this.ctx.objectInfo;
    const sizex = new Box3().setFromObject(bits.x1!).getSize(new Vector3());
    const sizey = new Box3().setFromObject(bits.y1!).getSize(new Vector3());
    const sizez = new Box3().setFromObject(bits.z1!).getSize(new Vector3());

    bits.x1!.position.set(center.x - size.x / 2 - sizex.x / 2, center.y, center.z);
    bits.x2!.position.set(center.x + size.x / 2 + sizex.x / 2, center.y, center.z);
    bits.y1!.position.set(center.x, center.y - size.y / 2 - sizey.y / 2, center.z);
    bits.y2!.position.set(center.x, center.y + size.y / 2 + sizey.y / 2, center.z);
    bits.z1!.position.set(center.x, center.y, center.z - size.z / 2 - sizez.z / 2);
    bits.z2!.position.set(center.x, center.y, center.z + size.z / 2 + sizez.z / 2);

    // Position tweens
    const positionTweenData: [Mesh, string, number][] = [
      [bits.x1!, 'x', bits.x1!.position.x - 20],
      [bits.x2!, 'x', bits.x2!.position.x + 20],
      [bits.y1!, 'y', bits.y1!.position.y - 20],
      [bits.y2!, 'y', bits.y2!.position.y + 20],
      [bits.z1!, 'z', bits.z1!.position.z - 20],
      [bits.z2!, 'z', bits.z2!.position.z + 20]
    ];
    for (const [mesh, axis, target] of positionTweenData) {
      tweens.push(
        new Tween(mesh.position)
          .to({ [axis]: target }, 1000)
          .easing(Easing.Cubic.InOut)
          .yoyo(true)
          .repeat(9999999999)
          .start()
      );
    }

    // Rotation tweens
    const rotationTweenData: [Mesh, string, number][] = [
      [bits.x1!, 'x', bits.x1!.rotation.x + 4],
      [bits.x2!, 'x', bits.x2!.rotation.x - 4],
      [bits.y1!, 'y', bits.y1!.rotation.y + 4],
      [bits.y2!, 'y', bits.y2!.rotation.y - 4],
      [bits.z1!, 'y', bits.z1!.rotation.y + 4],
      [bits.z2!, 'y', bits.z2!.rotation.y + 4]
    ];
    for (const [mesh, axis, target] of rotationTweenData) {
      tweens.push(
        new Tween(mesh.rotation)
          .to({ [axis]: target }, 1000)
          .yoyo(false)
          .repeat(9999999999)
          .start()
      );
    }
  }

  scaleMeshToDiameter(mesh: Mesh, desiredDiameter: number): number {
    const boundingBox = new Box3().setFromObject(mesh);
    const currentSize = new Vector3();
    boundingBox.getSize(currentSize);
    const currentDiameter = Math.max(currentSize.x, currentSize.z);
    return desiredDiameter / currentDiameter;
  }

  drillBitScaleChange(diameter: number): void {
    const bits = this.ctx.drillBits;
    const refMesh = this.ctx.drillbitMesh as Mesh;
    for (const key of Object.keys(bits)) {
      const d = bits[key as keyof typeof bits] as Mesh;
      const scaleFactor = this.scaleMeshToDiameter(refMesh, diameter);
      d.scale.set(scaleFactor, scaleFactor, scaleFactor);
    }
    this.drillBitUpdate();
  }
}
